import React, { useRef, useState } from "react";
import { slurpApi } from "../../lib/slurpApi";

const MAX_BYTES = 1024 * 1024; // 1 MB — backend cap matches this
const ACCEPT = "image/png,image/jpeg,image/webp,image/gif";

/**
 * Reusable image picker — file input + thumbnail + upload to R2 via the
 * existing /api/uploads/sign signing endpoint. On success calls onChange
 * with the public URL. Blank string clears the existing image.
 */
export default function ImagePicker({ value, onChange, prefix = "groups", disabled }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_BYTES) {
      setErr("Max 1 MB");
      return;
    }
    setErr(null);
    setBusy(true);
    try {
      const { uploadUrl, publicUrl } = await slurpApi.signUpload({
        contentType: file.type,
        size: file.size,
        prefix,
      });
      const put = await fetch(uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": file.type },
        body: file,
      });
      if (!put.ok) throw new Error(`R2 PUT ${put.status}`);
      onChange(publicUrl);
    } catch (e) {
      setErr(e?.message || "Upload failed");
    } finally {
      setBusy(false);
      // Reset so the same file can be re-picked after clearing.
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="pm-imgpick">
      {value ? (
        <div className="pm-imgpick__preview">
          <img src={value} alt="" />
          <button
            type="button"
            className="pm-imgpick__clear"
            onClick={() => onChange("")}
            disabled={busy || disabled}
            aria-label="Remove image"
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M2 2 L10 10 M10 2 L2 10"/></svg>
          </button>
        </div>
      ) : null}
      <button
        type="button"
        className="pm-imgpick__btn"
        onClick={() => inputRef.current?.click()}
        disabled={busy || disabled}
      >
        {busy ? "uploading…" : value ? "change image" : "+ add image"}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        onChange={onPick}
        style={{ display: "none" }}
      />
      <span className="pm-imgpick__hint">PNG / JPG / WebP / GIF · max 1 MB</span>
      {err && <span className="pm-imgpick__err">{err}</span>}
    </div>
  );
}
